// Drop zone for an input.upload block — browser → S3 presigned multipart
// (data/uploadService.js: resumable, any size); the resulting keys land in
// the block config so the run can read them.
import React, { useRef, useState } from 'react';
import { uploadFile, isUploadConfigured } from '../../data/uploadService';

const fmt = (n) => (n > 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1e3)} KB`);

export default function UploadBlockFields({ node, config, onConfig, orgId, pipelineId }) {
  const [busy, setBusy] = useState(null); // { name, pct }
  const [err, setErr] = useState('');
  const inputRef = useRef(null);
  const keys = String(config.keys || '').split('\n').map((k) => k.trim()).filter(Boolean);

  const addFiles = async (files) => {
    setErr('');
    const next = [...keys];
    for (const file of files) {
      const key = `items/nqube/${orgId}/${pipelineId}/${node.id}/${Date.now()}-${file.name.replace(/[^\w.-]+/g, '_')}`;
      try {
        setBusy({ name: file.name, pct: 0 });
        // eslint-disable-next-line no-await-in-loop
        const r = await uploadFile(file, { key, onProgress: (f) => setBusy({ name: file.name, pct: Math.round(f * 100) }) });
        next.push(r.key || key);
        onConfig({ ...config, keys: next.join('\n') });
      } catch (e) {
        setErr(`${file.name}: ${e.message}`);
        break;
      }
    }
    setBusy(null);
  };

  if (!isUploadConfigured()) return <p className="cs-help nq-soft-warn" style={{ fontSize: 12, marginTop: 8 }}>Uploads need the backend API (REACT_APP_UPLOAD_API).</p>;
  return (
    <div style={{ marginTop: 8 }}>
      <div
        className="nq-dropzone"
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
        onDrop={(e) => { e.preventDefault(); addFiles([...e.dataTransfer.files]); }}
        data-nova-note={`Drop zone for block ${node.id}: ${keys.length} file(s) attached.`}
      >
        {busy ? `Uploading ${busy.name} · ${busy.pct}%` : 'Drop files here or click to choose — any size, resumable'}
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => { addFiles([...e.target.files]); e.target.value = ''; }} />
      </div>
      {err && <p className="cs-help" style={{ fontSize: 12, color: 'var(--danger, #e5484d)', marginTop: 4 }} role="alert">{err}</p>}
      {keys.length > 0 && (
        <ul className="nq-filelist">
          {keys.map((k) => (
            <li key={k}>
              <span title={k}>{k.split('/').pop().replace(/^\d{13}-/, '')}</span>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onConfig({ ...config, keys: keys.filter((x) => x !== k).join('\n') })} title="Detach (the object stays in the bucket)">✕</button>
            </li>
          ))}
        </ul>
      )}
      <p className="cs-help" style={{ fontSize: 11.5, marginTop: 4 }}>Large files are not copied into the run: downstream blocks get a presigned URL per file ({fmt(5e9)}+ is fine).</p>
    </div>
  );
}
